import React, { useState } from "react";
import { Button, FormGroup, Input, InputGroup, Label } from "reactstrap";

export const EMPTY_PASSWORD = { mode: "generate", password: "", confirm: "" };

// What to send for a PasswordChoice value: null asks the server to generate
// one. Returns an error message instead when the typed passwords are unusable.
export const passwordToSend = ({ mode, password, confirm }) => {
  if (mode === "generate") return { password: null };
  if (!password) return { error: "Enter a password, or choose to generate one." };
  if (password !== confirm) return { error: "The passwords do not match." };
  return { password };
};

// Generate (the default) or type a password. Logto does not apply its password
// policy to passwords an admin sets, so the server checks the length; a
// generated one is always long and mixed.
export const PasswordChoice = ({ value, onChange, disabled }) => {
  const set = (changes) => onChange({ ...value, ...changes });

  return (
    <>
      <FormGroup tag="fieldset" className="mb-2">
        <Label className="d-block">Password</Label>
        <FormGroup check inline>
          <Input
            id="password-generate"
            type="radio"
            checked={value.mode === "generate"}
            onChange={() => set({ mode: "generate" })}
            disabled={disabled}
          />
          <Label check for="password-generate">
            Generate one
          </Label>
        </FormGroup>
        <FormGroup check inline>
          <Input
            id="password-choose"
            type="radio"
            checked={value.mode === "choose"}
            onChange={() => set({ mode: "choose" })}
            disabled={disabled}
          />
          <Label check for="password-choose">
            Choose one
          </Label>
        </FormGroup>
      </FormGroup>
      {value.mode === "choose" ? (
        <>
          <FormGroup>
            <Input
              aria-label="New password"
              placeholder="New password"
              type="password"
              autoComplete="new-password"
              value={value.password}
              onChange={(event) => set({ password: event.target.value })}
              disabled={disabled}
            />
          </FormGroup>
          <FormGroup className="mb-0">
            <Input
              aria-label="Confirm password"
              placeholder="Confirm password"
              type="password"
              autoComplete="new-password"
              value={value.confirm}
              onChange={(event) => set({ confirm: event.target.value })}
              disabled={disabled}
            />
          </FormGroup>
        </>
      ) : null}
    </>
  );
};

// A generated password, shown once. Nothing stores it, so closing the dialog
// is the last chance to copy it.
export const GeneratedPassword = ({ email, password }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the field is selectable instead.
    }
  };

  return (
    <>
      <p className="mb-2">
        The password for <strong>{email}</strong>:
      </p>
      <InputGroup>
        <Input
          readOnly
          value={password}
          onFocus={(event) => event.target.select()}
          style={{ fontFamily: "monospace" }}
          data-testid="generated-password"
        />
        <Button color="secondary" outline onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </Button>
      </InputGroup>
      <p className="mt-2 mb-0" style={{ color: "#575757" }}>
        It is shown only this once. Share it with them securely; they can change
        it under their email, top right.
      </p>
    </>
  );
};
